import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../../lib/utils";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { EmptyState, LoadingState, ui } from "../../../components/ui";
import { translateTicketStatus } from "../data/ticketConstants";
import { fetchComplaintLogs, sessionExpiredMessage } from "../../../services/fsmService";

const LOG_PAGE_SIZE = 20;

function formatLogDateTime(iso) {
  if (!iso) return "—";
  const date = new Date(String(iso).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${dd} ${months[date.getMonth()]} ${date.getFullYear()} ${hh}:${mm}`;
}

function displayValue(value) {
  if (value == null || value === "") return "—";
  return String(value);
}

function translateActorRole(role) {
  const t = i18n.t.bind(i18n);
  const key = String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (key === "manager") return t("common.manager");
  if (key === "technician") return t("fsm.technician");
  if (!role) return "—";
  return String(role).replace(/_/g, " ");
}

function formatActor(log) {
  const actorId = displayValue(log.actorId);
  const actorRole = translateActorRole(log.actorRole);
  if (actorId === "—" && actorRole === "—") return "—";
  return `${actorId}${actorRole !== "—" ? ` (${actorRole})` : ""}`;
}

export function ComplaintLogsModal({ open, onOpenChange, ticket }) {
  const complaintId = ticket?.complaintId ?? ticket?.id ?? "";
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [last, setLast] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const t = i18n.t.bind(i18n);
  const columns = [
  { key: "id", label: t("common.id"), className: "whitespace-nowrap" },
  { key: "createdOn", label: t("dateTime.createdOn"), className: "whitespace-nowrap" },
  { key: "action", label: t("common.action"), className: "whitespace-nowrap" },
  { key: "actor", label: t("common.employeeId"), className: "whitespace-nowrap" },
  { key: "from", label: t("common.from"), className: "whitespace-nowrap" },
  { key: "to", label: t("common.to"), className: "whitespace-nowrap" },
  { key: "assignedFrom", label: `${t("fsm.statusAssigned")} · ${t("common.from")}`, className: "whitespace-nowrap" },
  { key: "assignedTo", label: `${t("fsm.statusAssigned")} · ${t("common.to")}`, className: "whitespace-nowrap" },
  { key: "notes", label: t("common.notes"), className: "" }];

  useEffect(() => {
    if (!open || complaintId === "") {
      setLogs([]);
      setPage(0);
      setLast(true);
      setError("");
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError("");
      setLogs([]);
      setPage(0);
      try {
        const result = await fetchComplaintLogs(complaintId, {
          page: 0,
          size: LOG_PAGE_SIZE
        });
        if (cancelled) return;
        setLogs(result.items);
        setPage(result.page);
        setLast(result.last);
      } catch (err) {
        if (cancelled) return;
        setLogs([]);
        setError(
          sessionExpiredMessage(
            err,
            err.message || i18n.t("validation.unableToLoadComplaint")
          )
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, complaintId]);

  const handleLoadMore = async () => {
    if (loadingMore || last) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await fetchComplaintLogs(complaintId, {
        page: nextPage,
        size: LOG_PAGE_SIZE
      });
      setLogs((prev) => [...prev, ...result.items]);
      setPage(result.page);
      setLast(result.last);
    } catch (err) {
      console.error("Failed to load more complaint logs:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "w-[95vw] max-w-[1080px]",
            "max-h-[90vh] overflow-hidden flex flex-col",
            "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "font-sans"
          )}>
          
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-[var(--color-text-border)]/70 shrink-0">
            <div className="min-w-0">
              <DialogTitle className="text-[20px] font-semibold text-foreground tracking-tight">
                <Trans i18nKey="fsm.viewLogs" />
              </DialogTitle>
              {ticket ?
              <p className="mt-0.5 text-xs font-semibold text-[var(--color-primary)] truncate">
                  {i18n.t("fsm.complaintNumber")} - {ticket.complaintNo || ticket.id || "—"}
                </p> :
              null}
            </div>
            <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition">
              <X className="h-5 w-5" />
              <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 flex flex-col overflow-hidden">
            {loading ?
            <LoadingState message={i18n.t("common.loading")} className="p-6 sm:p-8" /> :
            error && logs.length === 0 ?
            <EmptyState
              title={i18n.t("validation.unableToLoadComplaint")}
              message={error}
              className="p-6 sm:p-8" /> :

            logs.length === 0 ?
            <EmptyState
              title={i18n.t("fsm.noResultsFound")}
              className="p-6 sm:p-8" /> :


            <>
                <div
                  className={cn(
                    "min-h-0 flex-1 overflow-x-scroll overflow-y-auto",
                    "[scrollbar-width:thin]",
                    "[scrollbar-color:var(--color-text-border)_var(--color-workspace)]",
                    "[&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5",
                    "[&::-webkit-scrollbar-track]:bg-[var(--color-workspace)]/60",
                    "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--color-text-border)]",
                    "[&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-transparent",
                    "[&::-webkit-scrollbar-thumb]:bg-clip-padding"
                  )}>
                  
                  <table className="w-full text-sm" style={{ minWidth: "960px" }}>
                    <thead>
                      <tr className={ui.tableHead}>
                        {columns.map((col) =>
                      <th
                        key={col.key}
                        className={cn("px-4 py-3 font-semibold", col.className)}>
                        
                            {col.label}
                          </th>
                      )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--color-text-border)]/35">
                      {logs.map((log) =>
                    <tr
                      key={log.logId ?? `${log.createdOn}-${log.action}`}
                      className="hover:bg-[var(--color-workspace)]/20 transition-colors">
                      
                          <td className="px-4 py-3 whitespace-nowrap font-semibold text-[var(--color-primary)]">
                            {displayValue(log.logId)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[var(--color-text-secondary)]">
                            {formatLogDateTime(log.createdOn)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-foreground">
                            {translateTicketStatus(log.action, t) || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-foreground">
                            {formatActor(log)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[var(--color-text-secondary)]">
                            {translateTicketStatus(log.oldStatus, t) || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-foreground">
                            {translateTicketStatus(log.newStatus, t) || "—"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[var(--color-text-secondary)]">
                            {displayValue(log.assignedFrom)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-[var(--color-text-secondary)]">
                            {displayValue(log.assignedTo)}
                          </td>
                          <td className="px-4 py-3 text-foreground min-w-[140px]">
                            {displayValue(log.notes)}
                          </td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </div>
                {!last ?
              <div className="px-6 py-3 border-t border-[var(--color-text-border)]/60 shrink-0">
                    <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="w-full h-10 rounded-xl border border-[var(--color-text-border)] text-sm font-semibold text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/40 transition disabled:opacity-50 cursor-pointer">
                  
                      {loadingMore ? i18n.t("fsm.loadingMore") : i18n.t("pagination.next2")}
                    </button>
                  </div> :
              null}
              </>
            }
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
